import { useEffect, useState } from 'react';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import { publicApi } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Leaf, Flame } from 'lucide-react';
import { useLanguage } from '@/lib/i18n';

export default function MenuPage() {
  const [menu, setMenu] = useState({ categories: [], formules: [] });
  const [settings, setSettings] = useState({});
  const [activeCategory, setActiveCategory] = useState(null);
  const [loading, setLoading] = useState(true);
  const { t } = useLanguage();

  useEffect(() => {
    publicApi.getMenu().then(r => {
      setMenu(r.data);
      if (r.data.categories?.length > 0) setActiveCategory(r.data.categories[0].id);
    }).catch(() => {}).finally(() => setLoading(false));
    publicApi.getSettings().then(r => setSettings(r.data || {})).catch(() => {});
  }, []);

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    return Number.isInteger(price) ? `${price} €` : `${price.toFixed(2).replace('.', ',')} €`;
  };

  return (
    <div className="min-h-screen bg-cream" data-testid="menu-page">
      <Navbar />
      <div className="pt-28 pb-20">
        {/* Header */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-12">
          <span className="text-sm font-medium tracking-wide uppercase text-garden-500 mb-2 block">{t('menu.sectionLabel')}</span>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-garden-800 mb-4">
            {t('menu.title')}
          </h1>
          <p className="text-base md:text-lg text-charcoal/70 max-w-2xl">
            {settings.menu_intro_text || t('menu.description')}
          </p>
        </div>

        {/* Category Tabs */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10">
          {loading ? (
            <div className="flex gap-2 overflow-x-auto pb-2">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="h-10 w-24 bg-garden-100 rounded-full animate-pulse shrink-0" />
              ))}
            </div>
          ) : (
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-none">
            {menu.categories.map((cat) => (
              <button
                key={cat.id}
                data-testid={`menu-category-${cat.slug}`}
                onClick={() => setActiveCategory(cat.id)}
                className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all shrink-0 ${
                  activeCategory === cat.id
                    ? 'bg-garden-800 text-white shadow-md'
                    : 'bg-white text-charcoal hover:bg-garden-100 border border-garden-200'
                }`}
              >
                {cat.name}
              </button>
            ))}
            <button
              data-testid="menu-category-formules"
              onClick={() => setActiveCategory('formules')}
              className={`whitespace-nowrap px-5 py-2.5 rounded-full text-sm font-medium transition-all shrink-0 ${
                activeCategory === 'formules'
                  ? 'bg-sunny text-charcoal shadow-md'
                  : 'bg-white text-charcoal hover:bg-yellow-50 border border-garden-200'
              }`}
            >
              {t('menu.formules')}
            </button>
          </div>
          )}
        </div>

        {/* Menu Items */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[1,2,3,4,5,6].map(i => (
                <div key={i} className="bg-white rounded-2xl p-5 border border-garden-800/5 shadow-sm animate-pulse">
                  <div className="flex justify-between mb-3">
                    <div className="h-5 w-32 bg-garden-100 rounded" />
                    <div className="h-5 w-14 bg-garden-100 rounded" />
                  </div>
                  <div className="h-4 w-full bg-garden-50 rounded mb-2" />
                  <div className="h-4 w-3/4 bg-garden-50 rounded" />
                </div>
              ))}
            </div>
          ) : activeCategory === 'formules' ? (
            <div className="grid sm:grid-cols-2 gap-6 max-w-3xl">
              {menu.formules.map((formule) => (
                <div key={formule.id} className="bg-white rounded-2xl p-6 border border-garden-800/5 shadow-sm menu-card" data-testid={`formule-${formule.id}`}>
                  <div className="flex justify-between items-start mb-3">
                    <h3 className="font-heading font-semibold text-xl text-garden-800">{formule.name}</h3>
                    <span className="font-heading font-bold text-2xl text-garden-800">{formatPrice(formule.price)}</span>
                  </div>
                  <ul className="space-y-1.5">
                    {formule.items?.map((item, i) => (
                      <li key={`${formule.id}-item-${i}`} className="text-sm text-charcoal/80 flex items-start gap-2">
                        <span className="text-garden-500 mt-1">+</span> {item}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          ) : (
            <>
              {menu.categories.filter(c => c.id === activeCategory).map((cat) => (
                <div key={cat.id}>
                  {cat.description && (
                    <p className="text-sm text-charcoal/60 mb-6 italic">{cat.description}</p>
                  )}
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {cat.items?.map((item) => (
                      <div key={item.id} className="bg-white rounded-2xl p-5 border border-garden-800/5 shadow-sm menu-card" data-testid={`menu-item-${item.id}`}>
                        <div className="flex justify-between items-start mb-2">
                          <h3 className="font-heading font-semibold text-lg text-garden-800 flex-1">{item.name}</h3>
                          {item.variants?.length > 0 ? (
                            <span className="text-sm text-garden-600 font-medium whitespace-nowrap ml-2">dès {formatPrice(item.price)}</span>
                          ) : (
                            <span className="font-heading font-bold text-lg text-garden-800 whitespace-nowrap ml-2">{formatPrice(item.price)}</span>
                          )}
                        </div>
                        {item.description && (
                          <p className="text-sm text-charcoal/70 mb-3 leading-relaxed">{item.description}</p>
                        )}
                        {item.variants?.length > 0 && (
                          <div className="flex flex-wrap gap-2 mb-2">
                            {item.variants.map((v) => (
                              <span key={`${item.id}-v-${v.name}`} className="text-xs bg-cream px-2.5 py-1 rounded-full text-charcoal/80">
                                {v.name}: <strong>{formatPrice(v.price)}</strong>
                              </span>
                            ))}
                          </div>
                        )}
                        {item.supplements?.length > 0 && (
                          <div className="mt-2 pt-2 border-t border-garden-100">
                            <span className="text-xs text-charcoal/50 block mb-1">{t('menu.supplements')}</span>
                            {item.supplements.map((s) => (
                              <span key={`${item.id}-s-${s.name}`} className="text-xs text-charcoal/70 mr-3">{s.name} +{formatPrice(s.price)}</span>
                            ))}
                          </div>
                        )}
                        {item.tags?.length > 0 && (
                          <div className="flex gap-1.5 mt-3">
                            {item.tags.map((tag) => (
                              <Badge key={tag} variant="secondary" className="text-xs bg-garden-100 text-garden-800 border-0">
                                {tag === 'végétarien' && <Leaf size={10} className="mr-1" />}
                                {tag === 'epicé' && <Flame size={10} className="mr-1" />}
                                {tag}
                              </Badge>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </>
          )}
        </div>

        {/* Accompagnements note */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mt-12">
          <div className="bg-cream-light rounded-2xl p-6 border border-garden-200">
            <h3 className="font-heading font-semibold text-garden-800 mb-2">{t('menu.sides')}</h3>
            <p className="text-sm text-charcoal/70">
              <strong>{t('menu.sidesIncluded')}</strong> {settings.menu_sides_included_text || t('menu.sidesIncludedText')}
            </p>
            <p className="text-sm text-charcoal/70 mt-1">
              <strong>{t('menu.sidesSupplements')}</strong> {settings.menu_sides_supplements_text || t('menu.sidesSupplementsText')}
            </p>
            <p className="text-xs text-charcoal/50 mt-3 italic">
              {settings.menu_sides_note || t('menu.sidesNote')}
            </p>
          </div>
        </div>
      </div>
      <p className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-xs text-charcoal/50 text-center">Prix nets exprimés en Euros TTC. Conformément au décret n° 2015-447, les informations sur les allergènes présents dans nos plats sont disponibles sur simple demande auprès de notre équipe.</p>
      <Footer />
    </div>
  );
}
